import { datadogLogs, type Logger as DDLogger, type StatusType } from '@datadog/browser-logs';
import { ApiError } from '@api/errors';
import type { LogContext, Logger } from './logger';
import { sanitize, truncateBody } from './sanitize';

export interface DatadogLoggerConfig {
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  verbose: boolean;
}

function initOnce(config: DatadogLoggerConfig): void {
  if (datadogLogs.getInitConfiguration()) return;
  datadogLogs.init({
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    version: config.version,
    sessionSampleRate: config.sessionSampleRate,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
  });
}

function normalizeError(error: unknown): { error?: Error; context: LogContext } {
  if (error === undefined || error === null) return { context: {} };
  if (error instanceof ApiError) {
    return {
      error,
      context: { errorName: 'ApiError', apiStatus: error.status, apiBody: truncateBody(error.body) },
    };
  }
  if (error instanceof Error) return { error, context: { errorName: error.name } };
  return { context: { errorValue: sanitize(error) } };
}

function emit(ddLogger: DDLogger, status: StatusType, message: string, parent: LogContext, context?: LogContext, error?: unknown): void {
  const { error: normalizedError, context: errorContext } = normalizeError(error);
  const merged = sanitize({ ...parent, ...(context ?? {}), ...errorContext }) as LogContext;
  ddLogger.log(message, merged, status, normalizedError);
}

function build(ddLogger: DDLogger, parentContext: LogContext, verbose: boolean): Logger {
  const noop = () => {};
  return {
    debug: verbose ? (message, context) => emit(ddLogger, 'debug', message, parentContext, context) : noop,
    info: verbose ? (message, context) => emit(ddLogger, 'info', message, parentContext, context) : noop,
    warn: (message, context, error) => emit(ddLogger, 'warn', message, parentContext, context, error),
    error: (message, context, error) => emit(ddLogger, 'error', message, parentContext, context, error),
    withContext: (context) => build(ddLogger, { ...parentContext, ...context }, verbose),
  };
}

export function createDatadogLogger(config: DatadogLoggerConfig): Logger {
  initOnce(config);
  return build(datadogLogs.logger, {}, config.verbose);
}
